import { useState } from "react";
import type { FormEvent } from "react";
import { Link } from "react-router-dom";
import SiteHeader from "@/components/feature/SiteHeader";
import SiteFooter from "@/components/feature/SiteFooter";
import usePageMeta from "@/hooks/usePageMeta";

const FORM_ADDR = "https://readdy.ai/api/form/dbb6j829ke8mc8ad7f2g";
const CONTACT_EMAIL = "contact@yinluanxiaozhen.example";

interface FormResponse {
  code?: string;
  message?: string;
  meta?: {
    message?: string;
    detail?: string;
  };
}

const scopes = [
  {
    icon: "ri-book-open-line",
    title: "完整剧情介绍",
    text: "不是三行简介，而是把起因、转折和结局写清楚的正文，帮你在观看前判断基调，观看后补充理解。",
  },
  {
    icon: "ri-play-list-2-line",
    title: "分集剧情目录",
    text: "剧集类作品提供逐集内容说明，可单独切换任意一集，方便追剧途中回看某一段的来龙去脉。",
  },
  {
    icon: "ri-filter-3-line",
    title: "多维筛选检索",
    text: "按类型、地区、年份、语言与题材组合筛选，也可以直接用关键词搜索片名、演员或导演。",
  },
];

export default function About() {
  usePageMeta(
    "关于本站 - 淫乱小镇影视内容",
    "了解淫乱小镇的内容定位、影视剧情介绍范围与编辑原则，并可通过站内反馈表单或邮箱与我们联系。",
    "淫乱小镇,关于本站,影视内容,剧情介绍,影视分析",
  );

  const [status, setStatus] = useState<"idle" | "submitting" | "success" | "error">("idle");
  const [formError, setFormError] = useState("");
  const [messageText, setMessageText] = useState("");
  const [copied, setCopied] = useState(false);

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(CONTACT_EMAIL);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2200);
    } catch {
      setCopied(false);
    }
  };

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);
    const honeypotValue = String(formData.get("website_alt") ?? "").trim();

    if (honeypotValue) {
      setStatus("success");
      setMessageText("");
      form.reset();
      return;
    }

    formData.delete("website_alt");
    setStatus("submitting");
    setFormError("");

    try {
      const body = new URLSearchParams();
      formData.forEach((value, key) => {
        if (typeof value === "string" && value.trim() !== "") {
          body.append(key, value);
        }
      });

      const response = await fetch(FORM_ADDR, {
        method: "POST",
        headers: { "Content-Type": "application/x-www-form-urlencoded" },
        body: body.toString(),
      });

      const responseText = await response.text();
      let parsed: FormResponse | null = null;
      try {
        parsed = JSON.parse(responseText) as FormResponse;
      } catch {
        parsed = null;
      }

      const serverMessage =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText;
      const isSpam =
        typeof serverMessage === "string" && serverMessage.toLowerCase().includes("spam");

      if (response.ok && parsed?.code === "OK" && !isSpam) {
        setStatus("success");
        setMessageText("");
        form.reset();
      } else {
        setStatus("error");
        setFormError(
          typeof serverMessage === "string" && serverMessage.trim()
            ? serverMessage
            : "提交没有成功，请稍后再试一次。",
        );
      }
    } catch {
      setStatus("error");
      setFormError("网络连接异常，请稍后重试，或直接通过邮箱联系我们。");
    }
  };

  return (
    <div className="min-h-screen bg-background-50">
      <SiteHeader />

      <main className="pt-16 md:pt-20">
        <section className="relative border-b border-background-200/70 bg-background-100/40 overflow-hidden">
          <div className="absolute -top-20 right-0 w-72 h-72 rounded-full bg-primary-500/20 blur-3xl"></div>
          <div className="absolute -bottom-24 left-1/4 w-64 h-64 rounded-full bg-accent-500/10 blur-3xl"></div>

          <div className="relative max-w-shell mx-auto px-4 md:px-8 py-10 md:py-16">
            <nav aria-label="面包屑导航" className="flex flex-wrap items-center gap-2 text-xs text-foreground-600">
              <Link to="/" className="hover:text-accent-500 transition-colors cursor-pointer">
                首页
              </Link>
              <i className="ri-arrow-right-s-line text-foreground-700"></i>
              <span className="text-foreground-300">关于本站</span>
            </nav>

            <span className="mt-6 block font-label text-[11px] tracking-[0.4em] text-accent-500 uppercase">
              About the Archive
            </span>
            <h1 className="mt-2 font-heading text-3xl md:text-5xl font-bold text-foreground-50">
              关于淫乱小镇
            </h1>
            <p className="mt-4 max-w-2xl text-sm md:text-base leading-relaxed text-foreground-400">
              一个把影视剧情讲清楚的内容站。我们相信，好的介绍不是把故事讲完，而是让你更想看，或者更想再想一遍。
            </p>

            <div className="mt-6 flex flex-wrap gap-x-8 gap-y-3 text-xs text-foreground-500">
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-map-pin-2-line text-accent-500"></i>
                内容由编辑团队独立整理
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-time-line text-accent-500"></i>
                每周更新作品与解析
              </span>
              <span className="inline-flex items-center gap-1.5">
                <i className="ri-shield-check-line text-accent-500"></i>
                不提供播放与下载
              </span>
            </div>
          </div>
        </section>

        <section className="max-w-shell mx-auto px-4 md:px-8 py-10 md:py-14">
          <div className="grid grid-cols-1 lg:grid-cols-[minmax(0,1fr)_380px] gap-8 lg:gap-12 items-start">
            <div className="min-w-0">
              <div className="rounded-lg border border-background-200/70 bg-background-100/50 p-5 md:p-7">
                <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                  我们在做什么
                </h2>
                <p className="mt-4 text-sm md:text-[15px] leading-8 text-foreground-400">
                  淫乱小镇开始于一个很小的想法：为什么很多影视介绍看完之后，还是不知道这部剧到底在讲什么？于是我们决定用另一种写法——把人物关系摆清楚，把关键转折标出来，把结局的意味说明白。
                </p>
                <p className="mt-3 text-sm md:text-[15px] leading-8 text-foreground-400">
                  目前站内收录了电影与剧集两类内容，覆盖国产、港台与海外作品。每部作品都有独立的详情页，剧集类还提供分集目录，方便你追到一半时回来对线索。
                </p>
              </div>

              <div id="scope" className="mt-8 scroll-mt-24">
                <div className="flex items-center gap-2.5 mb-5">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                    内容范围
                  </h2>
                </div>

                <div className="grid grid-cols-1 sm:grid-cols-3 gap-3 md:gap-4">
                  {scopes.map((scope) => (
                    <div
                      key={scope.title}
                      className="rounded-lg border border-background-200/70 bg-background-100/50 p-4 md:p-5"
                    >
                      <span className="w-10 h-10 rounded-md bg-primary-500/15 text-primary-600 flex items-center justify-center">
                        <i className={`${scope.icon} text-lg`}></i>
                      </span>
                      <h3 className="mt-3.5 font-heading text-base font-semibold text-foreground-50">
                        {scope.title}
                      </h3>
                      <p className="mt-2 text-xs leading-relaxed text-foreground-500">{scope.text}</p>
                    </div>
                  ))}
                </div>

                <div className="mt-4 rounded-lg border border-background-200/70 bg-background-50/60 p-4 md:p-5">
                  <p className="text-xs text-foreground-600 mb-2.5">以下内容不在本站范围内：</p>
                  <div className="flex flex-wrap gap-x-5 gap-y-2 text-xs text-foreground-400">
                    {["在线播放", "资源下载", "网盘分享", "付费观看", "用户投稿影片"].map((item) => (
                      <span key={item} className="inline-flex items-center gap-1.5">
                        <i className="ri-close-line text-foreground-700"></i>
                        {item}
                      </span>
                    ))}
                  </div>
                </div>
              </div>

              <div className="mt-8">
                <div className="flex items-center gap-2.5 mb-5">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-xl md:text-2xl font-bold text-foreground-50">
                    编辑原则
                  </h2>
                </div>
                <ol className="flex flex-col gap-4">
                  {[
                    {
                      title: "先讲清楚，再讲好看",
                      text: "所有介绍以信息准确为前提。不为了吸引点击而故意隐瞒关键信息，也不把未经证实的传闻写成结论。",
                    },
                    {
                      title: "结构化，而不是流水账",
                      text: "每篇解析都会区分背景、人物动机与叙事结构，让你能快速找到自己关心的那部分。",
                    },
                    {
                      title: "明确标注演示属性",
                      text: "站内作品与人物均为虚构设定，用于展示内容组织方式，不对应任何真实影视项目或从业人员。",
                    },
                  ].map((item, index) => (
                    <li key={item.title} className="flex gap-4">
                      <span className="font-label text-lg text-accent-500 leading-none pt-1 w-6 shrink-0">
                        {String(index + 1).padStart(2, "0")}
                      </span>
                      <div>
                        <h3 className="font-heading text-base font-semibold text-foreground-100">
                          {item.title}
                        </h3>
                        <p className="mt-1 text-xs md:text-sm leading-relaxed text-foreground-500">
                          {item.text}
                        </p>
                      </div>
                    </li>
                  ))}
                </ol>
              </div>
            </div>

            <div id="contact" className="scroll-mt-24">
              <div className="rounded-lg border border-background-200/70 bg-background-100/60 p-5 md:p-6">
                <div className="flex items-center gap-2.5">
                  <span className="w-1 h-5 rounded-full bg-accent-500"></span>
                  <h2 className="font-heading text-xl font-bold text-foreground-50">联系与反馈</h2>
                </div>
                <p className="mt-3 text-xs leading-relaxed text-foreground-500">
                  剧情纠错、内容建议或合作咨询都可以通过下面的表单提交，我们通常会在一周内查看。
                </p>

                <div className="mt-4 rounded-md border border-background-200/70 bg-background-50/60 p-3.5">
                  <p className="text-[11px] text-foreground-600">联系邮箱</p>
                  <div className="mt-1 flex items-center justify-between gap-2">
                    <span className="text-xs md:text-sm text-foreground-200 truncate">
                      {CONTACT_EMAIL}
                    </span>
                    <button
                      type="button"
                      onClick={handleCopy}
                      className="shrink-0 h-7 px-2.5 rounded border border-background-300 text-[11px] text-foreground-400 hover:border-accent-500 hover:text-accent-500 transition-colors cursor-pointer whitespace-nowrap"
                    >
                      {copied ? "已复制" : "复制"}
                    </button>
                  </div>
                </div>

                <form
                  className="contact-form mt-6 flex flex-col gap-3.5"
                  data-readdy-form
                  onSubmit={handleSubmit}
                >
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-3.5">
                    <div className="min-w-0">
                      <label
                        htmlFor="feedback-name"
                        className="block text-xs text-foreground-500 mb-1.5"
                      >
                        称呼
                      </label>
                      <input
                        id="feedback-name"
                        name="name"
                        type="text"
                        required
                        placeholder="怎么称呼你"
                        className="w-full h-10 box-border px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 placeholder:text-foreground-700 focus:outline-none focus:border-accent-500 transition-colors"
                      />
                    </div>
                    <div className="min-w-0">
                      <label
                        htmlFor="feedback-email"
                        className="block text-xs text-foreground-500 mb-1.5"
                      >
                        邮箱
                      </label>
                      <input
                        id="feedback-email"
                        name="email"
                        type="email"
                        required
                        placeholder="用于回复你"
                        className="w-full h-10 box-border px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 placeholder:text-foreground-700 focus:outline-none focus:border-accent-500 transition-colors"
                      />
                    </div>
                  </div>

                  <div className="min-w-0">
                    <label
                      htmlFor="feedback-topic"
                      className="block text-xs text-foreground-500 mb-1.5"
                    >
                      反馈类型
                    </label>
                    <select
                      id="feedback-topic"
                      name="topic"
                      defaultValue="剧情纠错"
                      className="w-full h-10 box-border px-3 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 focus:outline-none focus:border-accent-500 transition-colors cursor-pointer"
                    >
                      <option value="剧情纠错">剧情纠错</option>
                      <option value="内容建议">内容建议</option>
                      <option value="收录推荐">收录推荐</option>
                      <option value="合作咨询">合作咨询</option>
                    </select>
                  </div>

                  <div className="min-w-0">
                    <label
                      htmlFor="feedback-message"
                      className="block text-xs text-foreground-500 mb-1.5"
                    >
                      留言内容
                    </label>
                    <textarea
                      id="feedback-message"
                      name="message"
                      required
                      maxLength={500}
                      rows={4}
                      value={messageText}
                      onChange={(event) => setMessageText(event.target.value)}
                      placeholder="请简单说明你想反馈的内容，最多 500 字"
                      className="w-full box-border px-3 py-2.5 rounded-md bg-background-50 border border-background-300 text-sm text-foreground-100 placeholder:text-foreground-700 focus:outline-none focus:border-accent-500 transition-colors resize-none"
                    ></textarea>
                    <p className="mt-1 text-right text-[11px] text-foreground-700">
                      {messageText.length} / 500
                    </p>
                  </div>

                  <input
                    className="field-extra"
                    type="text"
                    name="website_alt"
                    tabIndex={-1}
                    autoComplete="off"
                    aria-hidden="true"
                    readOnly
                  />

                  <button
                    type="submit"
                    disabled={status === "submitting"}
                    className={`h-11 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                      status === "submitting"
                        ? "bg-background-300 text-foreground-600 cursor-wait"
                        : "bg-primary-500 text-foreground-50 hover:bg-primary-600"
                    }`}
                  >
                    {status === "submitting" ? "提交中..." : "提交反馈"}
                  </button>

                  {status === "success" && (
                    <p className="rounded-md border border-primary-300/50 bg-primary-500/10 px-3 py-2.5 text-xs text-foreground-200">
                      已收到你的反馈，感谢你帮助我们把内容做得更准确。
                    </p>
                  )}

                  {status === "error" && (
                    <p className="rounded-md border border-accent-500/40 bg-accent-500/10 px-3 py-2.5 text-xs text-accent-500">
                      {formError}
                    </p>
                  )}

                  <p className="text-[11px] leading-relaxed text-foreground-700">
                    提交即表示你同意我们仅将所填信息用于回复本次反馈。表单为演示用途，不会用于任何营销推送。
                  </p>
                </form>
              </div>

              <div
                id="policy"
                className="mt-5 rounded-lg border border-background-200/70 bg-background-100/40 p-5 scroll-mt-24"
              >
                <h2 className="font-heading text-base font-semibold text-foreground-100">使用须知与版权</h2>
                <ul className="mt-3 flex flex-col gap-2 text-xs leading-relaxed text-foreground-500">
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill text-[5px] mt-1.5 text-accent-500"></i>
                    站内影视作品、人物与剧情均为虚构设定，用于演示内容站的信息组织方式。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill text-[5px] mt-1.5 text-accent-500"></i>
                    站内文字内容为原创整理，转载或引用请注明来源并保留原文链接。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill text-[5px] mt-1.5 text-accent-500"></i>
                    本站不存储、不传播任何影视资源，也不提供播放、下载或网盘跳转。
                  </li>
                  <li className="flex gap-2">
                    <i className="ri-checkbox-blank-circle-fill text-[5px] mt-1.5 text-accent-500"></i>
                    如内容存在事实性错误，欢迎通过表单或邮箱指正，我们会尽快核对修改。
                  </li>
                </ul>
              </div>
            </div>
          </div>
        </section>
      </main>

      <SiteFooter />
    </div>
  );
}